import React from "react";
import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Bookmark, Loader2, X, Search } from "lucide-react";
import { toast } from "sonner";
import { sharedApi } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import Seo from "@/components/common/Seo";
import Breadcrumbs from "@/components/common/Breadcrumbs";

/**
 * Iter-294 — Saved searches page (/account/saved-searches).
 *
 * A dedicated surface that lists ONLY saved searches (job alerts,
 * category+keyword alerts, etc.). Category-follows continue to live on
 * /my/alerts. This page is deep-linkable from Account and elsewhere.
 *
 *  GET    /api/classifieds/saved-searches
 *  DELETE /api/classifieds/saved-searches/{id}
 */
export default function SavedSearchesPage() {
  const { user } = useAuth();
  const qc = useQueryClient();

  const q = useQuery({
    queryKey: ["saved-searches", user?.id],
    queryFn: async () => {
      const { data } = await sharedApi.get("/classifieds/saved-searches");
      return data?.saved_searches || [];
    },
    enabled: !!user,
    staleTime: 30_000,
    retry: 0,
  });

  const removeMut = useMutation({
    mutationFn: async (id) => sharedApi.delete(`/classifieds/saved-searches/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["saved-searches", user?.id] });
      qc.invalidateQueries({ queryKey: ["saved-searches"] });
      qc.invalidateQueries({ queryKey: ["my-follows", user?.id] });
      toast.success("Saved search removed.");
    },
    onError: (err) => toast.error(err?.response?.data?.detail || "Couldn't remove — please try again."),
  });

  const rows = q.data || [];
  const jobAlerts = rows.filter((r) => {
    const slug = (r.category_slug || "").toLowerCase();
    return slug === "jobs" || slug.startsWith("jobs/") || slug.startsWith("jobs-");
  });
  const otherAlerts = rows.filter((r) => !jobAlerts.includes(r));

  return (
    <div className="max-w-4xl mx-auto px-4 md:px-8 py-8 md:py-12">
      <Seo title="Saved searches · Allsale Classified" description="Keyword and region alerts you've saved — get emailed when new matches go live." />
      <Breadcrumbs items={[{ name: "Home", href: "/" }, { name: "Account", href: "/account" }, { label: "Saved searches" }]} />

      <div className="mt-6">
        <h1 className="font-display text-3xl md:text-4xl font-black text-slate-900" data-testid="saved-searches-title">Saved searches</h1>
        <p className="mt-2 text-slate-500">
          You&apos;ll get an email whenever a new listing matches one of these searches.
        </p>
      </div>

      {q.isLoading && (
        <div className="mt-10 flex items-center justify-center py-16 text-slate-400" data-testid="saved-searches-loading">
          <Loader2 className="w-6 h-6 animate-spin" />
        </div>
      )}

      {!q.isLoading && q.isError && (
        <div className="mt-10 rounded-2xl bg-white shadow-card p-10 text-center" data-testid="saved-searches-error">
          <h2 className="font-display text-lg font-bold text-slate-900">Couldn&apos;t load your saved searches</h2>
          <p className="mt-2 text-sm text-slate-500">Please try again in a moment.</p>
          <button
            type="button"
            onClick={() => q.refetch()}
            data-testid="saved-searches-retry"
            className="mt-5 inline-flex items-center gap-2 rounded-full bg-[color:var(--brand-blue)] hover:bg-[color:var(--brand-blue-hover)] text-white text-sm font-semibold px-5 py-2 transition-colors"
          >
            Retry
          </button>
        </div>
      )}

      {!q.isLoading && !q.isError && rows.length === 0 && (
        <div className="mt-10 rounded-2xl bg-white shadow-card p-12 text-center" data-testid="saved-searches-empty">
          <div className="w-14 h-14 mx-auto rounded-full bg-[color:var(--brand-blue-wash)] text-[color:var(--brand-blue)] grid place-items-center">
            <Bookmark className="w-6 h-6" />
          </div>
          <h2 className="mt-4 font-display text-xl font-bold text-slate-900">No saved searches yet</h2>
          <p className="mt-2 text-slate-500 max-w-md mx-auto">
            Tap &quot;Get job alerts&quot; on the jobs page, or &quot;Save this search&quot; on any results page.
          </p>
          <Link
            to="/c/jobs"
            data-testid="saved-searches-browse-jobs"
            className="mt-6 inline-flex items-center gap-2 rounded-full bg-[color:var(--brand-blue)] hover:bg-[color:var(--brand-blue-hover)] text-white font-semibold text-sm px-5 py-2.5 transition-colors"
          >
            Browse jobs
          </Link>
        </div>
      )}

      {!q.isLoading && !q.isError && rows.length > 0 && (
        <div className="mt-8 space-y-8">
          {jobAlerts.length > 0 && (
            <SearchSection
              title="Job alerts"
              items={jobAlerts}
              testIdPrefix="saved-job"
              onRemove={(id) => removeMut.mutate(id)}
              removingId={removeMut.isPending ? removeMut.variables : null}
            />
          )}
          {otherAlerts.length > 0 && (
            <SearchSection
              title="Other saved searches"
              items={otherAlerts}
              testIdPrefix="saved-other"
              onRemove={(id) => removeMut.mutate(id)}
              removingId={removeMut.isPending ? removeMut.variables : null}
            />
          )}
        </div>
      )}
    </div>
  );
}

function buildLink(s) {
  const params = new URLSearchParams();
  if (s.q) params.set("q", s.q);
  if (s.category_slug) params.set("category", s.category_slug);
  ["region", "city", "area", "make", "model", "seller_type"].forEach((k) => {
    if (s[k]) params.set(k, s[k]);
  });
  ["min_price", "max_price", "min_year", "max_year"].forEach((k) => {
    if (s[k] != null) params.set(k, String(s[k]));
  });
  if (Array.isArray(s.condition) && s.condition.length) params.set("condition", s.condition.join(","));
  // Iter-531 — restore EVERY filter: attribute multi-selects + ranges.
  Object.entries(s.attrs || {}).forEach(([k, v]) => {
    const vals = Array.isArray(v) ? v : [v];
    if (vals.length) params.set(`attr.${k}`, vals.join(","));
  });
  Object.entries(s.ranges || {}).forEach(([k, r]) => {
    if (r?.min != null) params.set(`attrmin.${k}`, String(r.min));
    if (r?.max != null) params.set(`attrmax.${k}`, String(r.max));
  });
  const qs = params.toString();
  return qs ? `/search?${qs}` : "/search";
}

function summarise(s) {
  const bits = [];
  if (s.category_name || s.category_slug) bits.push(s.category_name || s.category_slug);
  if (s.make || s.model) bits.push([s.make, s.model].filter(Boolean).join(" "));
  if (s.region) bits.push(s.region);
  if (s.city) bits.push(s.city);
  if (s.min_price != null || s.max_price != null) {
    bits.push(`$${s.min_price ?? 0}–$${s.max_price ?? "∞"}`);
  }
  if (s.min_year != null || s.max_year != null) {
    bits.push(`${s.min_year ?? "any"}–${s.max_year ?? "any"}`);
  }
  if (s.seller_type) bits.push(s.seller_type === "dealer" ? "Dealer" : "Private seller");
  const extra =
    Object.keys(s.attrs || {}).length + Object.keys(s.ranges || {}).length;
  if (extra > 0) bits.push(`+${extra} filter${extra > 1 ? "s" : ""}`);
  return bits.join(" · ") || "Any listing";
}

function SearchSection({ title, items, testIdPrefix, onRemove, removingId }) {
  return (
    <section data-testid={`${testIdPrefix}-section`}>
      <h2 className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400">{title}</h2>
      <div className="mt-3 space-y-3">
        {items.map((s) => {
          const busy = removingId === s.id;
          return (
            <div
              key={s.id}
              data-testid={`${testIdPrefix}-row-${s.id}`}
              className="rounded-2xl bg-white shadow-card p-4 md:p-5 flex items-center justify-between gap-4"
            >
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-11 h-11 rounded-xl grid place-items-center shrink-0 bg-emerald-50 text-emerald-600" aria-hidden="true">
                  <Search className="w-5 h-5" />
                </div>
                <div className="min-w-0">
                  <Link
                    to={buildLink(s)}
                    data-testid={`${testIdPrefix}-name-${s.id}`}
                    className="block font-display font-bold text-slate-900 hover:text-[color:var(--brand-blue)] transition-colors truncate"
                  >
                    {s.name || s.q || s.category_name || "Saved search"}
                  </Link>
                  <div className="mt-0.5 text-xs text-slate-500 truncate">
                    {summarise(s)}
                  </div>
                </div>
              </div>
              <button
                type="button"
                onClick={() => onRemove(s.id)}
                disabled={busy}
                data-testid={`${testIdPrefix}-remove-${s.id}`}
                className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold text-slate-600 hover:text-red-600 hover:bg-red-50 transition-colors disabled:opacity-60"
              >
                {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <X className="w-3.5 h-3.5" />}
                Remove
              </button>
            </div>
          );
        })}
      </div>
    </section>
  );
}
